import { startCase, toLower } from 'lodash/fp'
import md5 from 'md5'
import { useRouter } from 'next/router'
import { FC, useEffect } from 'react'
import styled from 'styled-components'

import { useAccountPageContext } from 'account/components/AccountPageContextProvider'
import { OrderIssuesPanel } from 'account/components/OrderIssuesPanel'
import { CreditCard } from 'account/models/BillingInfo'
import getShortDisplay from 'account/utils/getShortDisplay'
import { useRequestOrderTransferMutation } from 'api/ordersApi'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { t } from 'localization'
import { Button } from 'shared/components/Button'
import Image from 'shared/components/Image'
import { AccountQA } from 'shared/dataAttributes'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import PaymentType from 'shared/enums/PaymentType'
import { useAuth } from 'shared/hooks/useAuth'
import { useUser } from 'shared/hooks/useUser'
import { colors, fonts, media, showDesktop } from 'shared/lib'
import { formatMoneyObject } from 'shared/lib/formatMoneyObject'
import { HistoricalOrder } from 'shared/types/HistoricalOrder'
import { PartialOrder } from 'shared/types/Order'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'

const TP = 'account.components.orderSummaryCard'

const buyerCancellableStatuses = new Set([
  'awaiting_fc_review',
  'awaiting_payment',
  'canceled_by_seller_review',
  'goat_review',
  'goat_review_risk_rejected',
  'goat_review_skipped',
  'sold',
])

const isLessThan3Hours = (time: string) => {
  if (!time) {
    return false
  }
  const now = new Date()
  const then = new Date(time)
  if (now.getTime() - then.getTime() < 3 * 60 * 60 * 1000) {
    return true
  }
  return false
}

const getPaymentTypeDisplay = (payment: CreditCard) => {
  switch (payment?.paymentType) {
    case PaymentType.CreditCard:
      return `${payment?.cardBrand} *${payment?.last4Digits}`
    case PaymentType.Alipay:
      return 'Alipay'
    case PaymentType.Affirm:
      return 'Affirm'
    case PaymentType.AfterPay:
      return 'AfterPay'
    case PaymentType.PayPal:
    case PaymentType.PayPalv2:
      return 'PayPal'
    case PaymentType.ApplePay:
      return 'Apple Pay'
    case PaymentType.AndroidPay:
      return 'Android Pay'
    case PaymentType.Stripe:
      return 'Stripe'
    case PaymentType.Klarna:
      return 'Klarna'
    default:
      return ''
  }
}

type OrderSummaryCardProps = {
  hashedEmail?: string
  isLoggedInWithSameEmailAsOrder?: boolean
  order: PartialOrder | HistoricalOrder
  setErrors?: (errors) => void
  setIsLoading?: (input: boolean) => void
}

export const OrderSummaryCard: FC<OrderSummaryCardProps> = ({
  hashedEmail,
  isLoggedInWithSameEmailAsOrder,
  order,
  setErrors = () => null,
  setIsLoading = () => null,
}) => {
  const isGuestCheckoutEnabled = useFeatureFlag(FeatureFlag.GUEST_CHECKOUT)
  const isOrderCancellationEnabled = useFeatureFlag(FeatureFlag.WEB_FC_ORDER_CANCELLATION)
  const { currentUser, isAuthenticated } = useUser()
  const { logoutUser } = useAuth()
  const router = useRouter()
  const { locale } = useRouter()
  const [requestOrderTransfer, { isSuccess }] = useRequestOrderTransferMutation()
  const { availableCurrencies } = useShoppingRegionContext()

  if (!order) {
    return null
  }

  const { activeOrderNumber, setActiveOrderNumber } = useAccountPageContext()

  const {
    cardBrand,
    last4Digits,
    localizedCreditsUsedCents,
    localizedFinalPriceCents,
    number: orderNumber,
    paymentType,
    purchasedAt,
    status,
    buyerTitle,
  } = order

  // Retrieve currency from order instead of selectedCurrency
  const orderCurrency = availableCurrencies.filter((currency) => currency.isoCode === localizedFinalPriceCents?.currency)[0]

  const listPrice = order?.localizedListPriceCents || order?.item?.localizedItemSubtotalCents
  const presentation =
    order?.product?.sizeOption?.presentation || order?.item?.sizeOption?.presentation
  const name = order?.product?.productTemplate?.name || order?.item?.productTemplate?.name
  const details = order?.product?.productTemplate?.details || order?.item?.productTemplate?.details
  const pictureUrl =
    order?.product?.productTemplate?.pictureUrl || order?.item?.productTemplate?.pictureUrl
  const trackingNumber = order?.trackingToBuyerCode || order?.fcTrackingNumber
  const trackingUrl = order?.trackingToBuyerCodeUrl || order?.fcTrackingUrl

  const paymentTypeDisplay = getPaymentTypeDisplay({ paymentType, cardBrand, last4Digits })

  const handleLinkOrderToFullAccount = async () => {
    setIsLoading(true)
    setErrors([])
    try {
      await requestOrderTransfer(orderNumber)
    } catch (error) {
      setErrors(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleResolveIssueClick = async () => {
    setIsLoading(true)
    if (!isGuestCheckoutEnabled || !router.asPath.includes('/order-status')) {
      setActiveOrderNumber(orderNumber || null)
      setIsLoading(false)
      return
    }

    // everything below only pertains if guest checkout FF enabled AND path is `/order-status`
    if (isAuthenticated && !currentUser?.isGuest && hashedEmail !== md5(currentUser?.email || '')) {
      // logged in with email different from guest order email

      // first log out user
      await logoutUser()

      // redirect to manage order page
      setIsLoading(false)
      router.push(
        `/manage-order/${orderNumber}?q=${hashedEmail}&requiresLogin=true`,
        `/manage-order/${orderNumber}?q=${hashedEmail}&requiresLogin=true`,
        { locale },
      )
      return
    }
    if (isLoggedInWithSameEmailAsOrder) {
      // logged in with same email as guest order email
      setIsLoading(false)
      return handleLinkOrderToFullAccount()
    }
    if (!isAuthenticated || currentUser?.isGuest) {
      setIsLoading(false)
      router.push(
        `/manage-order/${orderNumber}?q=${hashedEmail}`,
        `/manage-order/${orderNumber}?q=${hashedEmail}`,
        { locale },
      )
    }
  }

  useEffect(() => {
    if (isSuccess) {
      const queryParams = orderNumber ? `?active-order=${orderNumber}` : ''
      router.replace(`/my-account${queryParams}`, `/my-account${queryParams}`, { locale })
    }
  }, [isSuccess])

  return (
    <Card>
      <Header>
        <div data-qa={AccountQA.OrderNumber}>
          {t(`${TP}.order`, 'Order #{orderNumber}', { orderNumber })}
        </div>
        <div data-qa={AccountQA.OrderDate}>{getShortDisplay(purchasedAt)}</div>
      </Header>
      <OrderDetails>
        <LineItem half={!paymentType}>
          <LineItemText data-qa={AccountQA.OrderTotalTag}>
            {t(`${TP}.orderTotal`, 'Order Total')}
          </LineItemText>
          <LineItemValue data-qa={AccountQA.OrderTotalAmount} bold>
            {formatMoneyObject(
              localizedFinalPriceCents,
              { disambiguatedCurrencySymbol: orderCurrency?.disambiguatedSymbol },
            )}
          </LineItemValue>
        </LineItem>
        {paymentType ? (
          <>
            <VertSpace />
            <LineGroup>
              <LineItem>
                <LineItemText data-qa={AccountQA.OrderPaymentMethodTag}>
                  {t(`${TP}.paymentMethod`, 'Payment Method')}
                </LineItemText>
                <LineItemValue data-qa={AccountQA.OrderPaymentMethodValue} bold uppercase>
                  {paymentTypeDisplay}
                </LineItemValue>
              </LineItem>
              {!!(localizedCreditsUsedCents && localizedCreditsUsedCents.amount) && (
                <LineItem secondLine>
                  <LineItemText>{t(`${TP}.flightClubCredit`, 'Flight Club Credit')}</LineItemText>
                  <LineItemValue data-qa={AccountQA.OrderFlightClubCredit} bold uppercase>
                    {formatMoneyObject(
                      localizedCreditsUsedCents,
                      { disambiguatedCurrencySymbol: orderCurrency?.disambiguatedSymbol },
                    )}
                  </LineItemValue>
                </LineItem>
              )}
            </LineGroup>
          </>
        ) : null}
      </OrderDetails>
      <ProductBody>
        <HorizontalDivider />
        <ProductDetails>
          <ProductSummary>
            <ImageContainer>
              <Image
                alt={name}
                data-qa={AccountQA.OrderShoeThumbnail}
                src={pictureUrl}
                width={104}
                height={74}
              />
            </ImageContainer>
            <ProductInformation>
              <ProductName data-testid="product-name" data-qa={AccountQA.OrderShoeName}>
                {startCase(toLower(name))}
              </ProductName>
              {presentation && (
                <SizeDetail>
                  <DetailTitle data-qa={AccountQA.OrderShoeSizeTag}>
                    {t(`${TP}.usSize`, 'US SIZE')}
                  </DetailTitle>
                  <span data-testid="product-size" data-qa={AccountQA.OrderShoeSizeValue}>
                    {presentation}
                  </span>
                </SizeDetail>
              )}
              <ProductDetailsLineTwo>
                <div data-qa={AccountQA.OrderShoeDetails}>{details}</div>
                <LineItemValue data-qa={AccountQA.OrderShoePrice} bold>
                  {formatMoneyObject(
                    listPrice,
                    { disambiguatedCurrencySymbol: orderCurrency?.disambiguatedSymbol },
                  )}
                </LineItemValue>
              </ProductDetailsLineTwo>
            </ProductInformation>
          </ProductSummary>
          <ProductShipping>
            {buyerTitle && (
              <LineItem>
                <LineItemText data-qa={AccountQA.OrderShippingStatusTag}>
                  {t(`${TP}.orderStatus`, 'Order Status')}
                </LineItemText>
                <LineItemValue
                  data-testid="order-status"
                  data-qa={AccountQA.OrderShippingStatusValue}
                  wordBreak
                >
                  {buyerTitle}
                </LineItemValue>
              </LineItem>
            )}
            {trackingNumber && (
              <LineItem secondLine>
                <LineItemText>{t(`${TP}.trackingNumber`, 'Tracking Number')}</LineItemText>
                <LineItemValue
                  data-testid="tracking-number"
                  data-qa={AccountQA.OrderTrackingNumber}
                  wordBreak
                >
                  {trackingNumber}
                </LineItemValue>
              </LineItem>
            )}
            {isOrderCancellationEnabled &&
              buyerCancellableStatuses.has(status) &&
              isLessThan3Hours(purchasedAt) && (
                <CancelOrderContainer>
                  <CancelOrderButton
                    onClick={handleResolveIssueClick}
                    data-qa={AccountQA.CancelOrderButton}
                  >
                    {t(`${TP}.cancelOrder`, 'Cancel Order')}
                  </CancelOrderButton>
                </CancelOrderContainer>
              )}
          </ProductShipping>
        </ProductDetails>
        {status === 'goat_issue' && (
          <>
            <ResolveIssueRow>
              <ResolveIssueButton
                data-testid="resolve-issue-button"
                onClick={handleResolveIssueClick}
              >
                {isLoggedInWithSameEmailAsOrder
                  ? t(`${TP}.linkOrder`, 'Link Order')
                  : t(`${TP}.viewIssue`, 'View Issue')}
              </ResolveIssueButton>
            </ResolveIssueRow>
          </>
        )}
        {activeOrderNumber === orderNumber && (
          <OrderIssuesPanel
            data-testid="order-issues-panel"
            isVisible={activeOrderNumber === orderNumber}
            onClose={() => {
              setActiveOrderNumber(null)
            }}
            orderNumber={orderNumber}
            paymentTypeDisplay={paymentTypeDisplay}
          />
        )}
        {trackingUrl && (
          <TrackingButtonWrapper>
            <a rel="noopener noreferrer" target="_blank" href={trackingUrl}>
              <TrackPackageButton fill>
                {t(`${TP}.trackPackage`, 'Track Package')}
              </TrackPackageButton>
            </a>
          </TrackingButtonWrapper>
        )}
      </ProductBody>
    </Card>
  )
}

const Card = styled.li`
  margin: 20px 0;
  border: 1px solid ${colors.FC2_LIGHTEST_GREY};
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px;
  ${fonts.SUBTITLE_3};
  background-color: ${colors.FC2_OFF_WHITE};
`

const Flex = styled.div`
  display: flex;
`

const VertSpace = styled.div`
  ${showDesktop``}
  height: 14px;
  margin: 0 8px;
`

const ImageContainer = styled.div`
  max-width: 77px;
  margin-right: 12px;
`

const LineItem = styled.div<{ half?: boolean; secondLine?: boolean }>`
  display: flex;
  justify-content: space-between;
  margin: 10px 0;
  ${media.large`
    width: ${({ half }) => (half ? '50%' : '100%')};
    margin: 0;
    ${({ secondLine }) => secondLine && 'margin-top: 10px;'}
  `}
`
const LineItemText = styled.div`
  ${fonts.BODY_TEXT}
  text-transform: uppercase;
  width: 50%;
  ${media.large`
    width: auto;
  `}
`
const LineGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`
const LineItemValue = styled.div<{ wordBreak?: boolean; bold?: boolean; uppercase?: boolean }>`
  /* add line height to match body text */
  line-height: 16px;
  ${fonts.SUBTITLE_3};
  ${({ bold }) => (bold ? 'font-weight: bold;' : '')}
  ${({ uppercase }) => uppercase && 'text-transform: uppercase;'}
  width: 50%;
  text-align: right;
  ${media.large`
    width: auto;
  `}
`

const HorizontalDivider = styled.div`
  margin: 20px;
  border-bottom: 1px solid ${colors.FC2_LIGHTEST_GREY};
`

const DetailTitle = styled.span`
  margin-right: 5px;
`

const SizeDetail = styled(Flex)`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_GREY};
`

const ProductDetailsLineTwo = styled(Flex)`
  margin-top: 14px;
  justify-content: space-between;
`

const ProductName = styled.div`
  ${fonts.SUBTITLE_2}
  margin-bottom: 5px;
`

const OrderDetails = styled.div`
  margin: 20px;
  ${media.large`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: start;
  `}
`
const ProductDetails = styled.div`
  margin: 20px;
  ${media.large`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  `}
`
const ProductInformation = styled.div`
  flex-grow: 1;
  ${fonts.BODY_TEXT}
`
const ProductSummary = styled.div`
  display: flex;
  margin: 20px 0;
  ${media.large`
    margin: 0 20px 0 0;
    flex-basis: 50%;
  `}
`
const ProductShipping = styled.div`
  ${media.large`
    margin: 0;
    flex-basis: 50%;
  `}
`

const TrackingButtonWrapper = styled.div`
  margin: 10px 20px 20px;
  ${media.large`
    margin: -20px 20px 20px;
    text-align: right;
  `}
`
const TrackPackageButton = styled(Button).attrs({
  buttonType: 'secondary',
})`
  ${media.large`
    width: 150px;
    height: 30px;
    line-height: 30px;
    min-height: auto;
  `}
`

const ResolveIssueRow = styled.div`
  margin: 0 20px 20px;
  display: flex;
  justify-content: flex-end;
`

const CancelOrderContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`

const CancelOrderButton = styled.button`
  margin-top: 36px;
  text-align: right;
  font-weight: 500;
  color: ${colors.FC2_RED};
`

/**
 * Note: Design requested some one off dimensions for this button
 * Design says this will be a secondary dimension style for buttons but leaving for
 * now until we have another example
 */
const ResolveIssueButton = styled(Button).attrs({
  buttonType: 'primary2',
})`
  width: 150px;
  height: 30px;
  line-height: 30px;
`

const ProductBody = styled.div``
